import { Navigate, Route, Routes } from 'react-router-dom'
import { RequireAuth, useAuth } from './lib/auth'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { MockStripePage } from './pages/MockStripePage'
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage'
import { EmbeddedCheckoutPage } from './pages/EmbeddedCheckoutPage'
import { Phase1View } from './pages/Phase1View'
import { Phase2View } from './pages/Phase2View'
import { Phase3View } from './pages/Phase3View'
import { Phase4View } from './pages/Phase4View'
import { ClosedView } from './pages/ClosedView'
import { BetsPage } from './pages/BetsPage'
import { SportsbookPage } from './pages/SportsbookPage'
import { StatsPage } from './pages/StatsPage'
import { PayoutPage } from './pages/PayoutPage'
import { KycPage } from './pages/KycPage'
import { SettingsPage } from './pages/SettingsPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { HelpPage } from './pages/HelpPage'
import { RiskDisclosurePage } from './pages/RiskDisclosurePage'
import { SystemStatusPage } from './pages/SystemStatusPage'
import { ProfilePage } from './pages/ProfilePage'
import { LeaderboardPage } from './pages/LeaderboardPage'
import { ChallengePage } from './pages/ChallengePage'
import { SettingsSecurityPage } from './pages/SettingsSecurityPage'
import { SettingsNotificationsPage } from './pages/SettingsNotificationsPage'
import { SettingsCommunicationPage } from './pages/SettingsCommunicationPage'
import { MatchDetailPage } from './pages/MatchDetailPage'
import { BillingPage } from './pages/BillingPage'
import { NotFoundPage } from './pages/NotFoundPage'

function PhaseRouter() {
  const { account, isLoading } = useAuth()
  if (isLoading) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
  // No challenge yet — show zero-state Phase1 dashboard. The /challenge route
  // (tier picker) remains reachable from the sidebar for the user to actually start.
  if (!account) return <Phase1View />
  switch (account.currentPhase) {
    case 'p1': return <Phase1View />
    case 'p2': return <Phase2View />
    case 'p3': return <Phase3View />
    case 'p4': return <Phase4View />
    case 'closed': return <ClosedView />
    default: return <Phase1View />
  }
}

const Auth = ({ children }: { children: React.ReactNode }) => <RequireAuth>{children}</RequireAuth>

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/risk-disclosure" element={<RiskDisclosurePage />} />
      <Route path="/system-status" element={<SystemStatusPage />} />

      <Route path="/onboarding" element={<Auth><OnboardingPage /></Auth>} />
      <Route path="/mock/stripe/checkout/:id" element={<Auth><MockStripePage /></Auth>} />
      <Route path="/checkout-success" element={<Auth><CheckoutSuccessPage /></Auth>} />
      <Route path="/checkout/embedded" element={<Auth><EmbeddedCheckoutPage /></Auth>} />

      <Route path="/" element={<Auth><PhaseRouter /></Auth>} />
      <Route path="/challenge" element={<Auth><ChallengePage /></Auth>} />
      <Route path="/verification" element={<Auth><Phase2View /></Auth>} />
      <Route path="/review" element={<Auth><Phase3View /></Auth>} />
      <Route path="/funded" element={<Auth><Phase4View /></Auth>} />
      <Route path="/closed" element={<Auth><ClosedView /></Auth>} />
      <Route path="/bets" element={<Auth><BetsPage /></Auth>} />
      <Route path="/sportsbook" element={<Auth><SportsbookPage /></Auth>} />
      <Route path="/stats" element={<Auth><StatsPage /></Auth>} />
      <Route path="/payout" element={<Auth><PayoutPage /></Auth>} />
      <Route path="/kyc" element={<Auth><KycPage /></Auth>} />
      <Route path="/settings" element={<Auth><SettingsPage /></Auth>} />
      <Route path="/settings/kyc" element={<Navigate to="/kyc" replace />} />
      <Route path="/settings/security" element={<Auth><SettingsSecurityPage /></Auth>} />
      <Route path="/settings/notifications" element={<Auth><SettingsNotificationsPage /></Auth>} />
      <Route path="/settings/communication" element={<Auth><SettingsCommunicationPage /></Auth>} />
      <Route path="/notifications" element={<Auth><NotificationsPage /></Auth>} />
      <Route path="/bets/:betId" element={<Auth><MatchDetailPage /></Auth>} />
      <Route path="/billing" element={<Auth><BillingPage /></Auth>} />
      <Route path="/help" element={<Auth><HelpPage /></Auth>} />
      <Route path="/profile" element={<Auth><ProfilePage /></Auth>} />
      <Route path="/leaderboard" element={<Auth><LeaderboardPage /></Auth>} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
